import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismIcon,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { range } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import ReviewCard from 'lumen/components/hotel/reviews/review-card';
import ReviewCardLoading from 'lumen/components/hotel/reviews/review-card-loading';
import type { Review } from 'lumen/models';

import styles from './sidebar.module.css';

interface HotelReviewsSidebarSignature {
  Args: {
    isLoading: boolean;
    isRefreshDisabled: boolean;
    onRefreshClick: () => void;
    otasWithErrors: string[];
    reviews: Review[];
    selectReviewForDetail: (reviewId: string) => void;
    selectedReviewId: string;
  };
}

const SKELETON_CARD_COUNT = 10;

function formatOtaListForDisplay(otas: string[]): string {
  return otas.join(', ');
}

<template>
  <div
    class="flex items-center justify-between pt-200 pr-200 pb-200 pl-400
      {{styles.sidebar-header}}"
  >
    <div class="flex items-center gap-100">
      <h4
        class="text-neutral-emphasis truncate"
        data-test-id="sidebar-title"
      >{{t "review_manager.sidebar.title"}}</h4>
      {{#if @otasWithErrors.length}}
        <div>
          <PrismIcon
            @color="warning"
            @name="warning"
            @size="small"
            data-test-id="ota-fetch-error-icon"
          />
          <PrismTooltip
            @side="top"
            @text={{t
              "review_manager.sidebar.warning.unable_to_load"
              otas=(formatOtaListForDisplay @otasWithErrors)
            }}
          />
        </div>
      {{/if}}
    </div>
    <PrismButton
      @buttonType="primary"
      @iconBefore="sync"
      @isDisabled={{@isRefreshDisabled}}
      @label={{t "general.refresh"}}
      @onClick={{@onRefreshClick}}
      data-test-id="refresh-button"
    />
  </div>
  <div class="flex flex-col gap-500 {{styles.scrollable-container}} relative">
    {{#if @isLoading}}
      {{#each (range 0 SKELETON_CARD_COUNT)}}
        <ReviewCardLoading />
      {{/each}}
    {{else}}
      {{#each @reviews as |review|}}
        <ReviewCard
          @isSelected={{eq review.id @selectedReviewId}}
          @review={{review}}
          @selectReviewForDetail={{@selectReviewForDetail}}
        />
      {{/each}}
      <p class="text-200--regular self-center">
        {{t "review_manager.sidebar.subtitle"}}
      </p>
    {{/if}}
  </div>
</template> satisfies TOC<HotelReviewsSidebarSignature>;
